// src/components/exercises/QuizExercise.jsx
import { useState, useEffect } from 'react';
import './Exercise.css';
import './QuizExercise.css';
import { launchConfetti, showAchievement } from '../utils/effects.js';
import { ArrowRight, CheckCircle2 } from 'lucide-react';

// <<< FUNCIÓN DE MEZCLA (SHUFFLE) >>>
// Algoritmo Fisher-Yates para mezclar arrays in-place
const shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

// FUNCIÓN PARA PARSEAR MARKDOWN (DOBLE ASTERISCO)
const parseAndHighlightText = (text) => {
  if (!text) return '';
  // Divide el texto en partes, separando lo que está en negrita.
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, index) => {
    // Las partes impares son las que estaban entre asteriscos.
    if (index % 2 === 1) {
      return <strong key={index} className="quiz-highlight-text">{part}</strong>;
    }
    return part;
  });
};


function QuizExercise({ exerciseData, onCorrectAnswer }) {
  const { title, instruction, questions } = exerciseData;

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [incorrectAttempts, setIncorrectAttempts] = useState([]);
  const [isCorrectlyAnswered, setIsCorrectlyAnswered] = useState(false);
  const [feedback, setFeedback] = useState({ message: '', type: '' });
  const [points, setPoints] = useState(0);
  const [streak, setStreak] = useState(0);
  const [timeLeft, setTimeLeft] = useState(exerciseData.timeLimit || 30);
  const [isTimerActive, setIsTimerActive] = useState(true);
  
  // <<< ESTADO PARA OPCIONES MEZCLADAS >>>
  const [shuffledOptions, setShuffledOptions] = useState([]);

  const currentQuestion = questions ? questions[currentQuestionIndex] : null;
  const totalQuestions = questions ? questions.length : 0;
  const progressPercentage = totalQuestions > 0 ? ((currentQuestionIndex) / totalQuestions) * 100 : 0;

  // <<< EFECTO PARA MEZCLAR OPCIONES CADA VEZ QUE CAMBIA LA PREGUNTA >>>
  useEffect(() => {
    if (currentQuestion && currentQuestion.options) {
      // Clonamos para evitar modificar el objeto original y mezclamos
      setShuffledOptions(shuffleArray([...currentQuestion.options]));
    }
  }, [currentQuestionIndex, currentQuestion]);


  useEffect(() => {
    if (!isTimerActive || isCorrectlyAnswered) return;
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timer);
          setIsTimerActive(false);
          setFeedback({ message: '¡Tiempo agotado!', type: 'incorrect' });
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [isTimerActive, isCorrectlyAnswered, currentQuestionIndex]);

  useEffect(() => {
    setTimeLeft(exerciseData.timeLimit || 30);
    setIsTimerActive(true);
    setFeedback({ message: '', type: '' });
  }, [currentQuestionIndex, exerciseData.timeLimit]);

  const handleOptionSelect = (option) => {
    if (isCorrectlyAnswered || !isTimerActive || !currentQuestion) return;
    
    // La validación Sigue usando currentQuestion.answer (el valor correcto)
    if (option === currentQuestion.answer) {
      setIsCorrectlyAnswered(true);
      setFeedback({ message: 'Correct! Good work.', type: 'correct' });
      setPoints(prevPoints => prevPoints + 10);
      setStreak(prevStreak => prevStreak + 1);
      setIsTimerActive(false);
    } else {
      if (!incorrectAttempts.includes(option)) {
        setIncorrectAttempts([...incorrectAttempts, option]);
      }
      setFeedback({ message: 'Wrong answer. Keep trying!', type: 'incorrect' });
      setStreak(0);
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex >= totalQuestions - 1) {
      showAchievement('¡Excelente trabajo en inglés!', '🎉');
      launchConfetti();
      onCorrectAnswer();
    } else {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setIncorrectAttempts([]);
      setIsCorrectlyAnswered(false);
    }
  };

  const getOptionClass = (option) => {
    if (!currentQuestion) return '';
    
    // La comprobación de la respuesta correcta sigue usando currentQuestion.answer
    if (isCorrectlyAnswered && option === currentQuestion.answer) return 'correct';
    if (incorrectAttempts.includes(option)) return 'incorrect';
    return '';
  };

  if (!currentQuestion) {
    return <div>Cargando pregunta...</div>;
  }

  return (
    <div className="exercise-container">
      <div className="qe-info-bar">
        <div className="qe-info-item qe-points">
          <span>🏆</span> {points} pts
        </div>
        <div className="qe-info-sub-bar">
          <div className="qe-info-item qe-streak">
            <span>⚡</span> Racha: {streak}
          </div>
          <div className={`qe-info-item qe-time ${timeLeft <= 5 ? 'accent-bg' : ''}`}>
            <span>🕒</span> {timeLeft}s
          </div>
        </div>
      </div>

      <div className="qe-progress-bar-container">
        <div className="qe-progress-bar-fill" style={{ width: `${progressPercentage}%` }}></div>
      </div>

      {/* Nuevo contenedor para el título y la instrucción */}
      <div className="qe-header-card">
        <h2 className="exercise-title">{title || 'English Grammar Quiz'}</h2>
        <p className="exercise-instruction">{instruction || 'Selecciona la respuesta correcta'}</p>
      </div>

      <div className="qe-question-wrapper">
        <div className="qe-question-number-badge">
          {currentQuestionIndex + 1}
        </div>
        <p className="qe-question-text">
          {parseAndHighlightText(currentQuestion.question)}
        </p>
      </div>

      <div className="qe-options">
        {/* MAPEO SOBRE LAS OPCIONES MEZCLADAS */}
        {shuffledOptions.map((option, index) => (
          <div
            key={index}
            className={`qe-option ${getOptionClass(option)}`}
            onClick={() => handleOptionSelect(option)}
          >
            <span className="qe-option-letter-badge">{String.fromCharCode(65 + index)}</span>
            {/* La validación de la clase sigue siendo correcta */}
            <span>{parseAndHighlightText(option)}</span>
          </div>
        ))}
      </div>

      {isCorrectlyAnswered && (
        <div className="qe-next-button-wrapper">
          <button className="exercise-action-button" onClick={handleNextQuestion}>
            {currentQuestionIndex < totalQuestions - 1 ? (
              <>Next Question <ArrowRight size={20} /></>
            ) : (
              <>Complete Quiz <CheckCircle2 size={20} /></>
            )}
          </button>
        </div>
      )}

      {feedback.message && (
        <div className={`qe-feedback ${feedback.type === 'correct' ? 'qe-feedback-correct' : 'qe-feedback-incorrect'}`}>
          {feedback.message}
        </div>
      )}
    </div>
  );
}

export default QuizExercise;